import React, { useState, useEffect } from 'react';
import {
  Avatar,
  Box,
  Button,
  Link,
  Dialog,
  Slider,
  Chip,
  Grid,
  IconButton,
  Stack,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  ListItemIcon,
  SvgIcon,
  Popover,
  List,
  ListItem,
  ListItemButton, Card, CardContent,
  ListItemText, FormControl, InputLabel, Select, MenuItem, Collapse, useMediaQuery, Drawer
} from '@mui/material';
import SlickSlider from 'react-slick';
import { useAuth } from 'hooks/useAuthContext';
import { useNavigate } from 'react-router-dom';
import { getJobList, getDashboardData } from 'api/job';
import { getBlogList } from 'api/blogs';
import { endpoints } from 'utils/axios';
import { getTimeDifferenceString, changeNameFormat, formatNumber, toTitleCase } from 'utils/Utils';
import dayjs from 'dayjs';
import { SplashScreen } from 'components/loading-screen';
import ExternalJob from './ExternalJob';
import SuperchargeCareer from './SuperchargeCareer';
import { SuitableChip, ModerateChip, NotSuitableChip } from '../icon/icon';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTheme } from '@mui/material/styles';

const JobNotMatchingIcon = `${process.env.REACT_APP_HOMEPAGE}assets/job-not-matching-icon.png`;
const ModSuitableIcon = `${process.env.REACT_APP_HOMEPAGE}assets/moderately-suitable-icon.png`;
const suitableIcon = `${process.env.REACT_APP_HOMEPAGE}assets/highly-suitable-icon.png`;
const likeIcon = `${process.env.REACT_APP_HOMEPAGE}assets/like-icon.png`;
const WavingHand = `${process.env.REACT_APP_HOMEPAGE}assets/waving-hand-icon.png`;

import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { init } from 'pdfmake/build/pdfmake';
const ThoughtsIcon = `${process.env.REACT_APP_HOMEPAGE}assets/thought-icon.png`;

/**
 * Dashboard component handles the main UI for the job application dashboard.
 * It fetches and displays job and blog data, manages application states, and
 * handles user interactions such as tab selection and viewing application details.
 * 
 * Props:
 * - onSelectTab: Function to handle tab selection, receives tab index and job details as arguments.
 * 
 * The component maintains several states:
 * - Job and blog data including allApplications, savedApplications, inprogressApplications, etc.
 * - UI states such as loading, visible, alignment, etc.
 * 
 * It also includes multiple functions for fetching data, handling UI interactions,
 * and updating application statuses and profile completion.
 */

const Dashboard = ({ onSelectTab }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const { user, updateContext } = useAuth();
  const [allApplications, setAllApplications] = useState([]);
  const [savedApplications, setSavedApplications] = useState([]);
  const [inprogressApplications, setInprogressApplications] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [stages, setStages] = useState(0);
  const [courseBlogs, setCourseBlogs] = useState([]);
  const [courseBlogCount, setCourseBlogCount] = useState(0);

  const [eventBlogs, setEventBlogs] = useState([]);
  const [eventBlogCount, setEventBlogCount] = useState(0);

  const [jobs, setJobs] = useState([]);
  const [visible, setVisible] = useState(true);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const [thoughtOfTheDay, setThoughtOfTheDay] = useState('');

  const [applicationStage, setApplicationStage] = useState([
    'Applied',
    'Shortlisted for interview',
    'Final selection',
    'Offer made',
    'Offer accepted'
  ]);

  const statusSlider = {
    dots: false,
    infinite: false,
    draggable: true,
    swipe: true,
    touchMove: true,
    swipeToSlide: true,
    speed: 500,
    arrows: true,
    // slidesToShow: (allApplications?.length > 0 ? (!(isMobile || isTablet) ? 3 : 2) : (!(isMobile || isTablet) ? 3 : 2)),
    // slidesToScroll: (allApplications?.length > 0 ? (!(isMobile || isTablet) ? 3 : 2) : (!(isMobile || isTablet) ? 3 : 2)),
    initialSlide: 0,
    variableWidth: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          // slidesToShow: 2,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 600,
        settings: {
          // slidesToShow: 1,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 480,
        settings: {
          // slidesToShow: 1,
          slidesToScroll: 1
        }
      }
    ]
  };

  const settings = {
    dots: false,
    infinite: false,
    draggable: true,
    swipe: true,
    touchMove: true,
    swipeToSlide: true,
    arrows: true,
    speed: 500,
    // slidesToShow: (allApplications?.length > 0 ? (!(isMobile || isTablet) ? 4 : 3) : (!(isMobile || isTablet) ? 4 : 3)),
    // slidesToScroll: (allApplications?.length > 0 ? (!(isMobile || isTablet) ? 3 : 2) : (!(isMobile || isTablet) ? 3 : 2)),
    variableWidth: true,
    initialSlide: 0,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          // slidesToShow: 2,
          slidesToScroll: 1,
          // initialSlide: 2,
        }
      },
      {
        breakpoint: 600,
        settings: {
          // slidesToShow: 2,
          slidesToScroll: 1,
          // initialSlide: 2
        }
      },
      {
        breakpoint: 480,
        settings: {
          // slidesToShow: 1,
          slidesToScroll: 1
        }
      }
    ]
  };
  const applicationSettings = {
    dots: false,
    infinite: false,
    draggable: true,
    swipe: true,
    touchMove: true,
    swipeToSlide: true,
    arrows: true,
    speed: 500,
    // slidesToShow: (allApplications?.length > 0 ? (!(isMobile || isTablet) ? 3 : 2) : (!(isMobile || isTablet) ? 3 : 2)),
    // slidesToScroll: (allApplications?.length > 0 ? (!(isMobile || isTablet) ? 2 : 2) : (!(isMobile || isTablet) ? 2 : 2)),
    variableWidth: true,
    initialSlide: 0, 
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          // slidesToShow: 2,
          slidesToScroll: 1,
          // initialSlide: 2
        }
      },
      {
        breakpoint: 600,
        settings: {
          // slidesToShow: 2,
          slidesToScroll: 1,
          // initialSlide: 2
        }
      },
      {
        breakpoint: 480,
        settings: {
          // slidesToShow: 1,
          slidesToScroll: 1
        }
      }
    ]
  };

  const [alignment, setAlignment] = useState('jobs-recommended');
  const [openActionPending, setOpenActionPending] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const slidesToShow = 4;

  /**
   * Fetches job data from the server and updates the component's state accordingly.
   * If the response is successful, it filters out recommended jobs and sets the jobs state.
   * It also sets the candidate's updated at timestamp and stages.
   * If the saved jobs array is empty, it sets the alignment state to 'jobs-recommended'.
   * Finally, it sets the all applications, saved applications, and in progress applications states.
   * If the response fails, it logs an error message to the console.
   */
  const fetchJobs = async () => {

    try {
      const response = await getDashboardData();
      if (response?.success && response?.data) {
        const { jobs = [], candidate, application = {} } = response.data;

        const nonSavedApps = application?.notSavedJobs || [];

        const nonAppliedJobs = jobs?.filter((job) => job?.recommended);
        setJobs(nonAppliedJobs);

        if (candidate) {
          setUpdatedAt(candidate?.updatedAt);
          setStages(candidate?.stages);
        }

        const savedApps = application?.savedJobs || [];
        setSavedApplications(savedApps);

        setAllApplications(nonSavedApps);
        const inProgressApplications = application?.pendingJobs || [];
        setInprogressApplications(inProgressApplications);

        if (nonAppliedJobs?.length === 0 && savedApps?.length > 0) setAlignment('jobs-saved');

      } else {
        console.error('Failed to fetch jobs:', response?.message);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }

  };

  /**
   * Fetches course and event blogs from the server.
   *
   * This function fetches the list of course and event blogs from the server and
   * updates the state accordingly. If the response fails, it logs an error message
   * to the console.
   */
  const fetchBlogs = async () => {
    try {
      const response = await getBlogList({ userType: user?.userType || 'user' });
      if (response?.success && response?.data) {
        setCourseBlogs(response?.data?.CourseBlogs || []);
        setCourseBlogCount(response?.data?.CourseBlogsCount || 0);
        setEventBlogs(response?.data?.EventBlogs || []);
        setEventBlogCount(response?.data?.EventBlogsCount || 0);
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
    }
  }

  // --- CHANGE 3: Create a new function to fetch the thought of the day ---
  const fetchThought = async () => {
    try {
      const endpointURL = `${process.env.REACT_APP_END_POINT}${endpoints.services.getThought}`;
      const response = await fetch(endpointURL);
      const result = await response.json();

      // Check if the request was successful and if data exists
      if (result.success && result.data) {
        setThoughtOfTheDay(result.data.thought);
      }
    } catch (error) {
      console.error("Failed to fetch thought of the day:", error);
    }
  };

  useEffect(() => {
    /**
     * Fetches all necessary dashboard data concurrently.
     * 
     * This function sets the loading state to true, then concurrently fetches
     * jobs, blogs, and the thought of the day using Promise.all. Once all data
     * is fetched, it sets the loading state to false. This ensures that the 
     * dashboard displays up-to-date information for jobs, blogs, and thought
     * of the day.
     */

    const fetchAllDashboardData = async () => {
      setLoading(true);


      await Promise.all([
        fetchJobs(),
        fetchBlogs(),
        fetchThought()
      ]);
      setLoading(false);
    };

    fetchAllDashboardData();
  }, []);

  /**
   * Handles changes to the ToggleButtonGroup.
   * When the toggle value changes, it updates the alignment state variable.
   * @param {React.ChangeEvent<HTMLInputElement>} event - The event from the ToggleButtonGroup.
   * @param {string} newAlignment - The new alignment value from the ToggleButtonGroup.
   */
  const handleToggleChange = (event, newAlignment) => {
    if (newAlignment !== null) {
      setAlignment(newAlignment);
    }
  };

  /**
   * Handles the change event when a recommended job category is selected.
   * It triggers the tab selection process for the recommended jobs, passing
   * the selected job as a parameter.
   *
   * @param {Object} job - The job object that was selected from the recommended category.
   */

  const handleCategoryRecommendedChange = (job) => {
    onSelectTab('2', job);
  };
  /**
   * Handles the click event when the "View All" button is clicked for
   * recommended jobs.
   *
   * This function triggers the tab selection process for the recommended
   * jobs, passing null as the job parameter.
   */
  const handleRecommendedViewAllClick = () => {
    onSelectTab('2', null);
  };

  /**
   * Handles the click event when the "View All" button is clicked for
   * saved external jobs.
   *
   * This function triggers the tab selection process for the saved
   * external jobs, passing null as the job parameter and the tab
   * identifiers as 'saved-jobs' and 'external'.
   */
  const handleApplicationViewAllClick = () => {
    onSelectTab('3', null, 'saved-jobs', 'external');
  };

  /**
   * Handles the click event when the "View All" button is clicked for
   * applied jobs.
   *
   * This function triggers the tab selection process for the applied
   * jobs, passing the first job from the allApplications state array
   * as the job parameter and the tab identifier as 'applied-jobs'.
   */
  const handleAppliedViewAllClick = () => {
    const { job_id, ...rest } = allApplications[0];

    const job = {
      ...rest,
      job_id: job_id?._id,
      jobDetails: job_id
    };

    onSelectTab('3', job, 'applied-jobs');
  };

  const handleCategorySavedChange = (application) => {
    const { job_id, ...rest } = application;

    const job = {
      ...rest,
      job_id: job_id?._id,
      jobDetails: job_id
    };

    onSelectTab('3', job, 'saved-jobs');
  };
  /**
   * Handles the click event when the "View All" button is clicked for
   * saved internal jobs.
   *
   * This function triggers the tab selection process for the saved
   * internal jobs, passing null as the job parameter and the tab
   * identifiers as '3', 'saved-jobs', and 'internal'.
   */
  const handleSavedViewAllClick = () => {
    onSelectTab('3', null, 'saved-jobs', 'internal');
  };
  const handleCategoryAppliedChange = (application) => {
    const { job_id, ...rest } = application;

    const job = {
      ...rest,
      job_id: job_id?._id,
      jobDetails: job_id
    };

    onSelectTab('3', job, 'applied-jobs');
  };
  const [open, setOpenPopover] = React.useState(null);
  const [anchorEl, setAnchorEl] = React.useState(null);

  /**
   * Handles the open event for a popover.
   *
   * This function is called when a popover should be opened. It sets the
   * anchor element to the element that triggered the event, and sets the
   * popover open state to the index of the popover.
   *
   * @param {React.MouseEvent<HTMLDivElement>} event - The event that triggered
   *   the popover open.
   * @param {number} index - The index of the popover to open.
   */
  const handlePopoverOpen = (event, index) => {
    setAnchorEl(event.currentTarget);
    setOpenPopover(index);
  };

  /**
   * Handles the close event for a popover.
   *
   * This function is called when a popover should be closed. It resets the
   * anchor element and the popover open state to null.
   */
  const handlePopoverClose = () => {
    setAnchorEl(null);
    setOpenPopover(null);
  };

  /**
   * Returns a string indicating the completeness of a candidate's profile.
   *
   * @param {number} profile - The completeness of the candidate's profile.
   *   1 for 25%, 2 for 50%, 3 for 75%, and 4 for 100%.
   * @returns {string} A string indicating the completeness of the
   *   candidate's profile.
   */
  const updateProfile = (profile) => {
    if (profile == 4) {
      return 'Your profile is 100% complete.';
    } else if (profile == 3) {
      return 'Your profile is 75% complete.';
    } else if (profile == 2) {
      return 'Your profile is 50% complete.';
    } else if (profile == 1) {
      return 'Your profile is 25% complete.';
    } else {
      return 'Your profile is 0% complete.';
    }
  };

  const recommendedChip = (score) => score >= 3 ? <SuitableChip /> : score >= 2 ? <ModerateChip /> : <NotSuitableChip />;

  /**
   * Determines if a given date is older than six months from the current date.
   *
   * @param {string|null} updatedAt - The date to be checked. If null, the function returns false.
   * @returns {boolean} True if the given date is older than six months from now, otherwise false.
   */

  const updateBeforeSixMonth = (updatedAt = null) => {
    if (!updatedAt) return false;
    const sixMonthsBeforeNow = dayjs().subtract(6, 'months');
    return dayjs(updatedAt).isBefore(sixMonthsBeforeNow);
  };
  /**
   * Calculates the stage of a job application based on the given status.
   *
   * @param {string} status - The status of the job application.
   *   One of 'applied', 'shortlisted', 'Final selection', 'offer made', or 'offer accepted'.
   * @returns {number} The stage of the job application.
   *   0 if the status is 'applied', 1 if 'shortlisted', 2 if 'Final selection',
   *   3 if 'offer made', 4 if 'offer accepted', and 0 otherwise.
   */
  const calculateStage = (status) => {
    switch (status) {
      case 'applied': return 0;
      case 'shortlisted for interview': return 1;
      case 'final selection': return 2;
      case 'offer made': return 3;
      case 'offer accepted': return 4;
      default: return 0;
    }
  };
  /**
   * Returns a string indicating the CSS class for the given application
   * stage in the list, based on the given status and index.
   *
   * @param {string} status - The status of the job application.
   *   One of 'applied', 'shortlisted', 'Final selection', 'offer made', or 'offer accepted'.
   * @param {number} index - The index of the application stage in the list.
   *   0 for 'applied', 1 for 'shortlisted', 2 for 'Final selection',
   *   3 for 'offer made', 4 for 'offer accepted'.
   * @returns {string} The CSS class for the given application stage in the list.
   *   'process-in-review' if the application is currently in review at this stage,
   *   'process-completed' if the application has been completed at this stage, or
   *   null if the application has not yet reached this stage.
   */
  const handleApplicationStageList = (status, index) => {
    let stage = calculateStage(status);
    if ((stage + 1) === index) {
      return 'process-in-review';
    } else if (stage >= index) {
      return 'process-completed';
    } else {
      return null;
    }
  };
  /**
   * Returns a start icon for the given application stage in the list, based on the given status and index.
   *
   * @param {string} status - The status of the job application.
   *   One of 'applied', 'shortlisted', 'Final selection', 'offer made', or 'offer accepted'.
   * @param {number} index - The index of the application stage in the list.
   *   0 for 'applied', 1 for 'shortlisted', 2 for 'Final selection', 3 for 'offer made', 4 for 'offer accepted'.
   * @returns {ReactElement} The start icon for the given application stage in the list.
   *   A Typography element with the index number if the application has not yet reached this stage, or
   *   a SvgIcon element with a tick icon if the application has reached this stage.
   */
  const handleApplicationStageStartIcon = (status, index) => {
    let stage = calculateStage(status);
    if (stage < index) {
      return <Typography variant="p">{index + 1}</Typography>;
    } else {
      return (
        <SvgIcon sx={{ width: '14px', height: '14px' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="8" height="6" viewBox="0 0 8 6" fill="none">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M7.40072 1.53905L3.51229 5.42748C3.24811 5.69166 2.81056 5.69166 2.53812 5.42748L0.598039 3.48739C0.333857 3.22321 0.333857 2.78566 0.598039 2.51322C0.862221 2.24904 1.29977 2.24904 1.57221 2.51322L3.03347 3.97448L6.43481 0.573136C6.69899 0.308955 7.13654 0.308955 7.40898 0.573136C7.6649 0.837318 7.6649 1.27487 7.40072 1.53905Z"
              fill="#0E7819"
              stroke="#E7FFEA"
              strokeWidth="0.6"
            />
          </svg>
        </SvgIcon>
      );
    }
  };
  /**
   * Calculates the value for the slider based on the given application status.
   *
   * @param {string} status - The status of the job application.
   *   One of 'applied', 'shortlisted', 'Final selection', 'offer made', or 'offer accepted'.
   * @returns {number} The value for the slider based on the given application status.
   *   0 for 'applied', 25 for 'shortlisted', 50 for 'Final selection', 75 for 'offer made', 100 for 'offer accepted', or 0 if the status is not recognized.
   */
  const calculateSliderValue = (status) => {
    switch (status) {
      case 'applied': return 0;
      case 'shortlisted for interview': return 25;
      case 'final selection': return 50;
      case 'offer made': return 75;
      case 'offer accepted': return 100;
      default: return 0;
    }
  };

  if (loading) {
    return (
      <Grid
        container
        justifyContent="center"
        sx={{ position: 'relative', overflow: 'hidden', width: '100%', height: 'calc(100vh - 10vh)' }}
        alignItems="center"
      >
        <SplashScreen />
      </Grid>
    );
  }

  /**
   * Toggles the open state of the actions pending list.
   */
  const handleOpenActionPending = () => {
    setOpenActionPending(!openActionPending);
  };

  return (
    <Box className="dashboard-page-wrapper" sx={{ backgroundColor: '#ffffff' }}>
      <Box
        className="dashboard-content-wrapper"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          height: 'calc(100vh - 110px)',
        }}
      >
        {!isMobile ? <Box className="actions-pending-wrapper" sx={{ height: allApplications?.length > 0 ? undefined : alignment === 'jobs-saved' ? savedApplications?.length > 0 ? '520px!important' : '240px!important' : jobs?.length > 0 ? '520px!important' : '240px!important' }}>
          <Box className="actions-pending-container">
            {inprogressApplications && inprogressApplications?.length > 0 && <Box className="actions-header">
              <List
                sx={{ width: '100%', p: 0 }}
                component="nav"
                aria-labelledby="nested-list-subheader"
              >
                <ListItemButton disableTouchRipple sx={{ p: 0 }} onClick={() => handleOpenActionPending()}>
                  <ListItemText primary={(updateBeforeSixMonth(updatedAt || null) && visible && inprogressApplications?.length > 0) ? "02" : "01" + " Actions Pending"} />
                  <Typography className="view-text">View</Typography>
                  {openActionPending ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>
                <Collapse in={openActionPending} timeout="auto" unmountOnExit>
                  {updateBeforeSixMonth(updatedAt || null) && visible && <List component="nav" disablePadding>
                    <ListItemButton sx={{ p: 0 }} disableTouchRipple onClick={() => navigate('/profile')}>
                      <Card className="actionCard" sx={{ width: '100%' }}>
                        <CardContent sx={{ p: '0!important', width: '100%' }}>
                          <Box>
                            <Stack direction="row" alignItems={'center'} justifyContent={'space-between'}>
                              <Typography className="title">Update Profile</Typography>
                              <SvgIcon sx={{ width: '16px', height: '16px' }} >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                  <path d="M5.96582 13.2827L10.3125 8.93599C10.8258 8.42266 10.8258 7.58266 10.3125 7.06932L5.96582 2.72266" stroke="#1575C6" stroke-width="1.33333" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                              </SvgIcon>
                            </Stack>
                          </Box>
                          <Box>
                            <Typography className="msg-text">Update your profile every 6 months</Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </ListItemButton>
                  </List>}

                  <List component="div" disablePadding>
                    <ListItemButton sx={{ p: 0 }} disableTouchRipple onClick={() => handleApplicationViewAllClick()}>
                      <Card className="actionCard" sx={{ width: '100%' }}>
                        <CardContent sx={{ p: '0!important', width: '100%' }}>
                          <Box>
                            <Stack direction="row" alignItems={'center'} justifyContent={'space-between'}>
                              <Typography className="title">Update Job Application Status</Typography>
                              <SvgIcon sx={{ width: '16px', height: '16px' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                  <path d="M5.96582 13.2827L10.3125 8.93599C10.8258 8.42266 10.8258 7.58266 10.3125 7.06932L5.96582 2.72266" stroke="#1575C6" stroke-width="1.33333" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                              </SvgIcon>
                            </Stack>
                          </Box>
                          <Box>
                            <Typography className="msg-text">Update status for {inprogressApplications?.length} job applications</Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </ListItemButton>
                  </List>
                </Collapse>
              </List>
            </Box>}
            {!isMobile && (
              <Box className="actions-thought-container">
                <Box className="thought-header" sx={{ marginBottom: '16px' }}>
                  <Stack direction="row" alignItems={'center'} justifyContent={'space-between'} spacing={2}>
                    <Typography className="title">Thought of the day</Typography>
                    <Box
                      mb={5}
                      component="img"
                      alt="Talent Junction"
                      width="25px"
                      height="25px"
                      src={ThoughtsIcon}
                    />
                  </Stack>
                </Box>


                <Typography className="thought-text">
                  {thoughtOfTheDay || '"The future belongs to those who believe in the beauty of their dreams." — Eleanor Roosevelt'}
                </Typography>
              </Box>
            )}
          </Box>
        </Box> :


          <Box className="actions-pending-wrapper">
            <Box className="actions-pending-container">
              {inprogressApplications && inprogressApplications?.length > 0 && <Box className="actions-header">
                <List
                  sx={{ width: '100%', p: 0 }}
                  component="nav"
                  aria-labelledby="nested-list-subheader"
                >
                  <ListItemButton disableTouchRipple sx={{ p: 0 }} onClick={() => handleOpenActionPending()}>
                    <ListItemText primary={(updateBeforeSixMonth(updatedAt || null) && visible && inprogressApplications?.length > 0) ? "02" : "01" + " Actions Pending"} />
                    <Typography className="view-text">View</Typography>
                    {openActionPending ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                  <Collapse in={openActionPending} timeout="auto" unmountOnExit>
                    {updateBeforeSixMonth(updatedAt || null) && visible && <List component="nav" disablePadding>
                      <ListItemButton sx={{ p: 0 }} disableTouchRipple onClick={() => navigate('/profile')}>
                        <Card className="actionCard" sx={{ width: '100%' }}>
                          <CardContent sx={{ p: '0!important', width: '100%' }}>
                            <Box>
                              <Stack direction="row" alignItems={'center'} justifyContent={'space-between'}>
                                <Typography className="title">Update Profile</Typography>
                                <SvgIcon sx={{ width: '16px', height: '16px' }} >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M5.96582 13.2827L10.3125 8.93599C10.8258 8.42266 10.8258 7.58266 10.3125 7.06932L5.96582 2.72266" stroke="#1575C6" stroke-width="1.33333" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                                  </svg>
                                </SvgIcon>
                              </Stack>
                            </Box>
                            <Box>
                              <Typography className="msg-text">Update your profile every 6 months</Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </ListItemButton>
                    </List>}

                    <List component="div" disablePadding>
                      <ListItemButton sx={{ p: 0 }} disableTouchRipple onClick={() => handleApplicationViewAllClick()}>
                        <Card className="actionCard" sx={{ width: '100%' }}>
                          <CardContent sx={{ p: '0!important', width: '100%' }}>
                            <Box>
                              <Stack direction="row" alignItems={'center'} justifyContent={'space-between'}>
                                <Typography className="title">Update Job Application Status</Typography>
                                <SvgIcon sx={{ width: '16px', height: '16px' }}>
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M5.96582 13.2827L10.3125 8.93599C10.8258 8.42266 10.8258 7.58266 10.3125 7.06932L5.96582 2.72266" stroke="#1575C6" stroke-width="1.33333" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                                  </svg>
                                </SvgIcon>
                              </Stack>
                            </Box>
                            <Box>
                              <Typography className="msg-text">Update status for {inprogressApplications?.length} job applications</Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </ListItemButton>
                    </List>
                  </Collapse>
                </List>
              </Box>}
            </Box>
          </Box>
        }

        <Box className="dashboard-candi-title">
          <Stack direction="row" alignItems={'center'} spacing={1}>
            <img alt="name" width="36px" height="36px" src={WavingHand} />
            <Typography className="candidate-name">Welcome {changeNameFormat(user?.name)}</Typography>
          </Stack>
        </Box>

        {allApplications && allApplications?.length > 0 && (
          <>
            <Box className="track-job-applications" mt={4} sx={{ width: isMobile ? '100%' : (isTablet ? `calc(100% - 140px)` : `calc(100% - 140px)`) }}>
              <Box className="track-app-card-block">
                <Box mb={3}>
                  {allApplications && allApplications?.length > 0 && (
                    <>
                      <Typography className="text-main" mb={0.5}>
                        Track your recent job applications
                      </Typography>
                      <Typography className="text-sub">
                        You have {allApplications?.length} active job applications
                        <Link sx={{ 'cursor': 'pointer' }} underline="none" className="view-all" ml={1} display={'inline-flex'} onClick={handleAppliedViewAllClick} >
                          View All{' '}
                          <SvgIcon sx={{ width: '14px', height: '14px' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path
                                d="M5.9668 2.7193L10.3135 7.06596C10.8268 7.5793 10.8268 8.4193 10.3135 8.93263L5.9668 13.2793"
                                stroke="#1575C6"
                                stroke-width="1.33333"
                                stroke-miterlimit="10"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </svg>
                          </SvgIcon>
                        </Link>
                      </Typography>
                    </>
                  )}
                </Box>
                <Box sx={{
                  paddingBottom: '40px',
                  '.slick-arrow.slick-disabled': {
                    opacity: 0.3,
                    pointerEvents: 'none',
                  },
                }}>
                  <SlickSlider
                    {...statusSlider}
                    className="exp-new-opp-slider shift-right-arrow"
                    arrows={true}
                    style={{ width: isMobile ? '100%' : (isTablet ? `calc(100% - 180px)` : `calc(100% - 180px)`) }}
                  >
                    {allApplications &&
                      allApplications?.map((item, index) => (
                        <Box className="card-wrapper" sx={{ width: '100% !important' }} key={index} onClick={() => handleCategoryAppliedChange(item)}>
                          <Box className="card-content-top" >
                            <Stack
                              direction="row"
                              alignItems={'center'}
                              justifyContent={'space-between'}
                              sx={{ width: '100%' }}
                            >
                              <Typography className="job-title">{item?.job_id?.title || ''}</Typography>
                              <IconButton className="arrow-btn" sx={{ padding: 0 }}>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="17"
                                  viewBox="0 0 16 17"
                                  fill="none"
                                >
                                  <path
                                    d="M1.84066 8.9703L12.8552 8.9703L9.37689 12.2584C9.1595 12.4639 9.1595 12.8064 9.37689 13.0119C9.59428 13.2174 9.9566 13.2174 10.174 13.0119L14.5218 8.83329C14.5943 8.76479 14.6667 8.62779 14.6667 8.49079C14.6667 8.35378 14.5943 8.21678 14.5218 8.14828L10.174 3.96968C10.1015 3.90118 9.9566 3.83268 9.81168 3.83268C9.66675 3.83268 9.52182 3.90118 9.44936 3.96968C9.23197 4.17519 9.23197 4.5177 9.44936 4.7232L12.9276 8.01128L1.91313 8.01128C1.62327 8.01128 1.33341 8.21678 1.33341 8.55929C1.33341 8.9018 1.55081 8.9703 1.84066 8.9703Z"
                                    fill="#111827"
                                  />
                                </svg>
                              </IconButton>
                            </Stack>
                            <Typography className="job-company" mb={1.5}>
                              {item?.job_id?.is_company_name_confidential ? 'Company Name - Confidential' : item?.job_id?.company || ''}
                            </Typography>
                            <Typography className="applied-days">
                              {item?.applied_at
                                ? getTimeDifferenceString(item?.applied_at, 'Applied')
                                : 'Application Pending'}
                            </Typography>
                          </Box>
                          <Box className="card-content-bottom application-pending custom-card-bottom-margin">
                            <Chip className="app-pending-chip" label="Application Pending" />
                          </Box>
                          <Box className="card-content-bottom custom-card-bottom-margin">
                            <Button endIcon={
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M13.0331 0.644197C13.1728 0.702374 13.2891 0.818726 13.3473 0.95835H13.3531C13.3822 1.02816 13.3997 1.10379 13.3997 1.17942V4.62928C13.3997 4.94925 13.1379 5.21104 12.8179 5.21104C12.4979 5.21104 12.2361 4.94925 12.2361 4.62928V2.58147L7.41331 7.40429C7.29696 7.51482 7.15152 7.573 7.00026 7.573C6.849 7.573 6.70356 7.52064 6.58721 7.40429C6.36032 7.1774 6.36032 6.81089 6.58721 6.584L11.41 1.76118H9.36222C9.04225 1.76118 8.78046 1.49939 8.78046 1.17942C8.78046 0.85945 9.04225 0.597656 9.36222 0.597656H12.8121C12.8877 0.597656 12.9633 0.615109 13.0331 0.644197ZM12.23 7.001C12.23 6.68103 12.4918 6.41924 12.8118 6.41924C13.1318 6.41924 13.3936 6.68103 13.3936 7.001V11.0443C13.3936 12.3416 12.3348 13.4004 11.0374 13.4004H2.95673C1.6594 13.4004 0.600586 12.3416 0.600586 11.0443V2.96356C0.600586 1.66623 1.6594 0.607422 2.95673 0.607422H6.99999C7.31996 0.607422 7.58175 0.869215 7.58175 1.18919C7.58175 1.50916 7.31996 1.77095 6.99999 1.77095H2.95673C2.29934 1.77095 1.76411 2.30617 1.76411 2.96356V11.0443C1.76411 11.7017 2.29934 12.2369 2.95673 12.2369H11.0374C11.6948 12.2369 12.23 11.7017 12.23 11.0443V7.001Z" fill="white" />
                              </svg>
                            }>
                              Track Status
                            </Button>
                          </Box>
                          <Box
                            className="card-content-bottom track-status-block custom-card-bottom-margin"
                            aria-owns={open ? 'track-job-status' : undefined}
                            aria-haspopup="true"
                            onMouseEnter={(event) => !isMobile && handlePopoverOpen(event, index)}
                            onMouseLeave={!isMobile && handlePopoverClose}
                          >
                            <Stack
                              direction="row"
                              justifyContent={'space-between'}
                              alignItems={'center'}
                              spacing={4}
                              sx={{ width: '100%' }}
                            >
                              <Chip className="current-track-status-chip" label={changeNameFormat(item?.status)} />
                              <Box sx={{ width: '105px' }}>
                                <Slider
                                  key={index}
                                  disabled
                                  className="track-status-slider"
                                  aria-label="Custom marks"
                                  defaultValue={calculateSliderValue(item?.status)}
                                  step={25}
                                  marks
                                  max={100}
                                  componentsProps={{
                                    thumb: {
                                      sx: {
                                        border: '3px solid #fff',
                                        backgroundColor: '#3d1c9b',
                                      }
                                    }
                                  }}
                                />
                              </Box>
                            </Stack>
                            <Popover
                              elevation={3}
                              id={`track-job-status-${index}`}
                              className="job-track-menu-list"
                              sx={{ pointerEvents: 'none' }}
                              open={open === index}
                              anchorEl={anchorEl}
                              anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'center',
                              }}
                              transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left',
                              }}
                              onClose={handlePopoverClose}
                              disableRestoreFocus
                            >
                              <Box className="track-list">
                                <List sx={{ width: 220 }}>
                                  {applicationStage?.map((stageName, stageIndex) => (
                                    <ListItem
                                      disablePadding
                                      key={stageIndex}
                                      className={handleApplicationStageList(item?.status, stageIndex)}
                                    >
                                      <ListItemButton>
                                        <ListItemIcon>
                                          {handleApplicationStageStartIcon(item?.status, stageIndex)}
                                        </ListItemIcon>
                                        <ListItemText sx={{ margin: 0 }} primary={stageName} />
                                      </ListItemButton>
                                    </ListItem>
                                  ))}
                                </List>
                              </Box>
                            </Popover>
                          </Box>
                        </Box>
                      ))}
                  </SlickSlider>
                </Box>
              </Box>
            </Box>
          </>)}

        <Box className="explore-new-opp">
          <Box className={`explore-new-opp-container ${allApplications?.length > 0 && 'bg-light-blue'}`} sx={{ width: allApplications?.length > 0 ? '100% !important' : (isMobile ? "100% !important" : "calc(100% - 320px) !important") }}>
            <Box>
              {allApplications?.length > 0 &&
                <Typography className="title" mb={3}>
                  Explore New Opportunities
                </Typography>
              }

              <Box mb={2}>
                <ToggleButtonGroup
                  className="jobs-toggle-btn"
                  color="primary"
                  value={alignment}
                  exclusive
                  onChange={handleToggleChange}
                  aria-label="Platform"
                >
                  <ToggleButton value="jobs-saved">Saved Jobs
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -9,
                        right: 4,
                        backgroundColor: '#c02424',
                        color: 'white',
                        borderRadius: '50%',
                        fontSize: 10,
                        width: 16,
                        height: 16,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                   {savedApplications?.length}
                  </Box>
                  </ToggleButton>
                  <ToggleButton value="jobs-recommended">Recommended Jobs
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -9,
                        right: 4,
                        backgroundColor: '#c02424',
                        color: 'white',
                        borderRadius: '50%',
                        fontSize: 10,
                        width: 16,
                        height: 16,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                   {jobs?.length}
                  </Box>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {alignment === 'jobs-saved' && (
                <Stack direction="row" alignItems={'center'} justifyContent={'space-between'} sx={{ width: '100%' }}>
                  <Box>
                    <Typography className="text-1"> You have {savedApplications?.length} saved job(s)
                      <Link
                        sx={{ 'cursor': 'pointer' }}
                        underline="none"
                        className="view-all"
                        ml={1}
                        display={'inline-flex'}
                        onClick={() => handleSavedViewAllClick()}
                      >
                        View All{' '}
                        <SvgIcon sx={{ width: '14px', height: '14px' }}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M5.9668 2.7193L10.3135 7.06596C10.8268 7.5793 10.8268 8.4193 10.3135 8.93263L5.9668 13.2793"
                              stroke="#1575C6"
                              stroke-width="1.33333"
                              stroke-miterlimit="10"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                        </SvgIcon>
                      </Link>
                    </Typography>
                  </Box>
                </Stack>
              )}
              {alignment === 'jobs-recommended' && (
                <Stack direction="row" alignItems={'center'} justifyContent={'space-between'} sx={{ width: '100%' }}>
                  <Box>
                    <Typography className="text-1">
                      We have {jobs?.length} recommended jobs for you
                      <Link
                        sx={{ 'cursor': 'pointer' }}
                        underline="none"
                        className="view-all"
                        ml={1}
                        display={'inline-flex'}
                        onClick={() => handleRecommendedViewAllClick()}
                      >
                        View All{' '}
                        <SvgIcon sx={{ width: '14px', height: '14px' }}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                          >
                            <path
                              d="M5.9668 2.7193L10.3135 7.06596C10.8268 7.5793 10.8268 8.4193 10.3135 8.93263L5.9668 13.2793"
                              stroke="#1575C6"
                              stroke-width="1.33333"
                              stroke-miterlimit="10"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                        </SvgIcon>
                      </Link>
                    </Typography>
                  </Box>
                </Stack>
              )}
            </Box>
            {alignment === 'jobs-saved' && savedApplications && savedApplications?.length > 0 && (() => {
              const currentSettings = allApplications?.length > 0 ? settings : applicationSettings;
              const showArrows = true // savedApplications.length > currentSettings.slidesToShow;
              return (
                <Box sx={{
                  '.slick-arrow.slick-disabled': {
                    opacity: 0.3,
                    pointerEvents: 'none',
                  }
                }}>
                  <SlickSlider {...currentSettings} arrows={showArrows}>
                    {savedApplications.map((item, index) => (
                      <Box className="card-wrapper" sx={{ width: '100% !important' }} key={item._id || index}>
                        <Box onClick={() => handleCategorySavedChange(item)}>
                          <Box className="card-content-top">
                            {recommendedChip(item?.tj_score)}
                            <Stack
                              direction="row"
                              alignItems={'center'}
                              justifyContent={'space-between'}
                              sx={{ width: '100%' }}
                            >
                              <Typography className="job-title">{item?.job_id?.title || ''}</Typography>
                              <IconButton className="arrow-btn" sx={{ padding: 0 }}>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="17"
                                  viewBox="0 0 16 17"
                                  fill="none"
                                >
                                  <path
                                    d="M1.84066 8.9703L12.8552 8.9703L9.37689 12.2584C9.1595 12.4639 9.1595 12.8064 9.37689 13.0119C9.59428 13.2174 9.9566 13.2174 10.174 13.0119L14.5218 8.83329C14.5943 8.76479 14.6667 8.62779 14.6667 8.49079C14.6667 8.35378 14.5943 8.21678 14.5218 8.14828L10.174 3.96968C10.1015 3.90118 9.9566 3.83268 9.81168 3.83268C9.66675 3.83268 9.52182 3.90118 9.44936 3.96968C9.23197 4.17519 9.23197 4.5177 9.44936 4.7232L12.9276 8.01128L1.91313 8.01128C1.62327 8.01128 1.33341 8.21678 1.33341 8.55929C1.33341 8.9018 1.55081 8.9703 1.84066 8.9703Z"
                                    fill="#111827"
                                  />
                                </svg>
                              </IconButton>
                            </Stack>

                            <Typography className="job-company">{item?.job_id?.is_company_name_confidential ? 'Company Name - Confidential' : item?.job_id?.company || ''}</Typography>
                            <Box mb={1} className="work-type">
                              <Stack direction="row" alignItems={'center'} spacing={0.5}>
                                <Typography variant="p" className="city">{item?.job_id?.location}</Typography>
                                <Typography variant="body1">•</Typography>
                                <Typography variant="p" className="city">{item?.job_id?.job_type}</Typography>
                                <Typography variant="body1">•</Typography>
                                <Typography variant="p" className="city">{item?.job_id?.work_mode}</Typography>
                              </Stack>
                            </Box>

                            <Box mb={2}>
                              <Stack direction="row" spacing={1}>
                                <Stack spacing={0.5}>
                                  {item?.job_id?.is_compensation_confidential ? <>
                                    <Typography variant="p" className="package">Not Disclosed</Typography>
                                    <Typography variant="body1" className="package-type">Compensation</Typography></> : <>
                                    <Typography variant="p" className="package">₹{formatNumber(item?.job_id?.salary)}</Typography>
                                    <Typography variant="body1" className="package-type">{item?.job_id?.compensation}</Typography></>}
                                </Stack>
                                <Box></Box>
                              </Stack>
                            </Box>
                            <Typography className="posted-date">
                              {item?.job_id?.updatedAt ? getTimeDifferenceString(item?.job_id?.updatedAt, 'Posted') : 'N/A'}
                            </Typography>
                          </Box>

                          <Box className="card-content-bottom">
                            <Button
                              endIcon={
                                item?.job_id?.external_link ? (
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="14"
                                    height="14"
                                    viewBox="0 0 14 14"
                                    fill="none"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M13.0331 0.644197C13.1728 0.702374 13.2891 0.818726 13.3473 0.95835H13.3531C13.3822 1.02816 13.3997 1.10379 13.3997 1.17942V4.62928C13.3997 4.94925 13.1379 5.21104 12.8179 5.21104C12.4979 5.21104 12.2361 4.94925 12.2361 4.62928V2.58147L7.41331 7.40429C7.29696 7.51482 7.15152 7.573 7.00026 7.573C6.849 7.573 6.70356 7.52064 6.58721 7.40429C6.36032 7.1774 6.36032 6.81089 6.58721 6.584L11.41 1.76118H9.36222C9.04225 1.76118 8.78046 1.49939 8.78046 1.17942C8.78046 0.85945 9.04225 0.597656 9.36222 0.597656H12.8121C12.8877 0.597656 12.9633 0.615109 13.0331 0.644197ZM12.23 7.001C12.23 6.68103 12.4918 6.41924 12.8118 6.41924C13.1318 6.41924 13.3936 6.68103 13.3936 7.001V11.0443C13.3936 12.3416 12.3348 13.4004 11.0374 13.4004H2.95673C1.6594 13.4004 0.600586 12.3416 0.600586 11.0443V2.96356C0.600586 1.66623 1.6594 0.607422 2.95673 0.607422H6.99999C7.31996 0.607422 7.58175 0.869215 7.58175 1.18919C7.58175 1.50916 7.31996 1.77095 6.99999 1.77095H2.95673C2.29934 1.77095 1.76411 2.30617 1.76411 2.96356V11.0443C1.76411 11.7017 2.29934 12.2369 2.95673 12.2369H11.0374C11.6948 12.2369 12.23 11.7017 12.23 11.0443V7.001Z"
                                      fill="white"
                                    />
                                  </svg>
                                ) : null
                              }
                            >
                              Apply Now
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </SlickSlider>
                </Box>
              )
            })()}
            {alignment === 'jobs-recommended' && jobs && jobs?.length > 0 && (() => {
              const currentSettings = allApplications?.length > 0 ? settings : applicationSettings;
              const showArrows = true // jobs.length > currentSettings.slidesToShow;
              return (
                <Box sx={{
                  '.slick-arrow.slick-disabled': {
                    opacity: 0.3,
                    pointerEvents: 'none',
                  }
                }}>
                  <SlickSlider {...currentSettings} arrows={showArrows}>
                    {jobs.map((item, index) => (
                      <Box className="card-wrapper" sx={{ width: '100% !important' }} key={item._id || index} onClick={() => handleCategoryRecommendedChange(item)}>
                        <Box className="card-content-top" >
                          {recommendedChip(item?.tj_score)}
                          <Stack
                            direction="row"
                            alignItems={'center'}
                            justifyContent={'space-between'}
                            sx={{ width: '100%' }}
                          >
                            <Typography className="job-title">{item?.title || ''}</Typography>
                            <IconButton className="arrow-btn" sx={{ padding: 0 }}>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="17"
                                viewBox="0 0 16 17"
                                fill="none"
                              >
                                <path
                                  d="M1.84066 8.9703L12.8552 8.9703L9.37689 12.2584C9.1595 12.4639 9.1595 12.8064 9.37689 13.0119C9.59428 13.2174 9.9566 13.2174 10.174 13.0119L14.5218 8.83329C14.5943 8.76479 14.6667 8.62779 14.6667 8.49079C14.6667 8.35378 14.5943 8.21678 14.5218 8.14828L10.174 3.96968C10.1015 3.90118 9.9566 3.83268 9.81168 3.83268C9.66675 3.83268 9.52182 3.90118 9.44936 3.96968C9.23197 4.17519 9.23197 4.5177 9.44936 4.7232L12.9276 8.01128L1.91313 8.01128C1.62327 8.01128 1.33341 8.21678 1.33341 8.55929C1.33341 8.9018 1.55081 8.9703 1.84066 8.9703Z"
                                  fill="#111827"
                                />
                              </svg>
                            </IconButton>
                          </Stack>

                          <Typography className="job-company">{item?.is_company_name_confidential ? 'Company Name - Confidential' : item?.company || ''}</Typography>
                          <Box mb={1} className="work-type">
                            <Stack direction="row" alignItems={'center'} spacing={0.5}>
                              <Typography variant="p" className="city">{item?.location}</Typography>
                              <Typography variant="body1">•</Typography>
                              <Typography variant="p" className="city">{item?.job_type}</Typography>
                              <Typography variant="body1">•</Typography>
                              <Typography variant="p" className="city">{item?.work_mode}</Typography>
                            </Stack>
                          </Box>

                          <Box mb={2}>
                            <Stack direction="row" spacing={1}>
                              <Stack spacing={0.5}>
                                {item?.is_compensation_confidential ? <>
                                  <Typography variant="p" className="package">Not Disclosed</Typography>
                                  <Typography variant="body1" className="package-type">Compensation</Typography></> : <>
                                  <Typography variant="p" className="package">₹{formatNumber(item?.salary)}</Typography>
                                  <Typography variant="body1" className="package-type">{item?.compensation}</Typography></>}
                              </Stack>
                              <Box>
                              </Box>
                            </Stack>
                          </Box>
                          <Typography className="posted-date">
                            {item?.updatedAt ? getTimeDifferenceString(item?.updatedAt, 'Posted') : 'N/A'}
                          </Typography>
                        </Box>

                        <Box className="card-content-bottom">
                          <Button
                            endIcon={
                              item?.external_link ? (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="14"
                                  height="14"
                                  viewBox="0 0 14 14"
                                  fill="none"
                                >
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M13.0331 0.644197C13.1728 0.702374 13.2891 0.818726 13.3473 0.95835H13.3531C13.3822 1.02816 13.3997 1.10379 13.3997 1.17942V4.62928C13.3997 4.94925 13.1379 5.21104 12.8179 5.21104C12.4979 5.21104 12.2361 4.94925 12.2361 4.62928V2.58147L7.41331 7.40429C7.29696 7.51482 7.15152 7.573 7.00026 7.573C6.849 7.573 6.70356 7.52064 6.58721 7.40429C6.36032 7.1774 6.36032 6.81089 6.58721 6.584L11.41 1.76118H9.36222C9.04225 1.76118 8.78046 1.49939 8.78046 1.17942C8.78046 0.85945 9.04225 0.597656 9.36222 0.597656H12.8121C12.8877 0.597656 12.9633 0.615109 13.0331 0.644197ZM12.23 7.001C12.23 6.68103 12.4918 6.41924 12.8118 6.41924C13.1318 6.41924 13.3936 6.68103 13.3936 7.001V11.0443C13.3936 12.3416 12.3348 13.4004 11.0374 13.4004H2.95673C1.6594 13.4004 0.600586 12.3416 0.600586 11.0443V2.96356C0.600586 1.66623 1.6594 0.607422 2.95673 0.607422H6.99999C7.31996 0.607422 7.58175 0.869215 7.58175 1.18919C7.58175 1.50916 7.31996 1.77095 6.99999 1.77095H2.95673C2.29934 1.77095 1.76411 2.30617 1.76411 2.96356V11.0443C1.76411 11.7017 2.29934 12.2369 2.95673 12.2369H11.0374C11.6948 12.2369 12.23 11.7017 12.23 11.0443V7.001Z"
                                    fill="white"
                                  />
                                </svg>
                              ) : null
                            }
                          >
                            Apply Now
                          </Button>
                        </Box>
                      </Box>
                    ))}
                  </SlickSlider>
                </Box>
              )
            })()}
          </Box>
        </Box>

        <Box className="supercharge-your-career" >
          <Box className={`supercharge-career-container ${!(allApplications?.length > 0) && 'bg-light-blue'}`}>
            <Box mb={3}>
              <Typography className="title">Learning Corner</Typography>
            </Box>
            <SuperchargeCareer />
          </Box>
        </Box>
      </Box>

    </Box >
  );
};

export default Dashboard;